/**
 * @fileoverview Flags from your own source — your backend, your SSR seed.
 * @module lib/features/core/flagSource
 *
 * `createFlags()` talks to FlowGrid's flag API. This talks to *yours*.
 *
 * The case is common enough to be a first-class feature rather than something
 * every app rebuilds: the answers are already known somewhere other than the
 * FlowGrid bundle. Perhaps a server evaluated them against rules that must never
 * ship to a browser (staff lists, percentage targeting mid-rollout); perhaps
 * they came from a legacy flag service; perhaps they were rendered into the page
 * so the very first paint is correct. All of those need identical machinery —
 * a synchronous read, a subscription, a refresh, a "ready" signal — and it is
 * machinery we already wrote and test. Rewriting it per app is how a flag read
 * ends up throwing on a page nobody tested with the network off.
 *
 * ```ts
 * import { createFlagSource, setFlagEngine } from 'flowgrid-sdk';
 *
 * setFlagEngine(createFlagSource({
 *   endpoint: '/api/my-flags',            // → { "some-flag": true }
 *   initial:  window.__FLOWGRID_FLAGS__,  // rendered by the server, optional
 * }));
 * ```
 *
 * Framework-agnostic on purpose: no imports beyond types, no DOM assumptions
 * beyond an optional `fetch`, and every method safe on the server. The React
 * binding consumes it through the registry; a legacy page can use
 * it directly from a script tag through `window.FlowGridFlags`.
 *
 * ## Guarantees it shares with the main engine
 *
 * `on()` is synchronous, never throws, and returns the caller's own default
 * until there is a real answer. A failed refresh keeps the last good snapshot
 * rather than blanking it — losing answers you already had is strictly worse
 * than serving them a minute stale. `ready()` always settles, including when
 * there is nothing to fetch, because a UI holding a paint on `ready()` must
 * never hang on a source that legitimately has no work to do.
 */

/** Evaluated answers, keyed by flag. Exactly what a source returns. */
export type FlagSnapshot = Record<string, boolean>;

export interface FlagSourceOptions {
  /**
   * Answers known at construction — an SSR seed, a cached bundle, a test
   * fixture. Present in the very first synchronous `on()`, which is what stops a
   * released feature rendering hidden for a frame and then popping in.
   */
  initial?: FlagSnapshot;

  /**
   * Where to fetch a snapshot from. Must return JSON that is either the snapshot
   * itself (`{ "flag": true }`) or wraps it under `flags` / `data.flags`, so a
   * standard `{ ok, data }` envelope works without an adapter.
   */
  endpoint?: string;

  /**
   * Full control over fetching, when `endpoint` is too simple — signed requests,
   * a non-HTTP source, a client you already have. Wins over `endpoint`.
   */
  fetcher?: () => Promise<FlagSnapshot>;

  /**
   * Re-fetch every N ms. Off by default, and that default is deliberate: a flag
   * flip is usually a rollout, not a live control, and polling every open tab
   * for a value that changes twice a week is a cost with no reader.
   */
  refreshMs?: number;

  /**
   * Skip the fetch on construction. The seed (if any) still answers immediately;
   * call `refresh()` when you want the first network read.
   */
  lazy?: boolean;

  /** Called when a refresh fails. Default: swallow — a flag read must not throw. */
  onError?: (error: unknown) => void;
}

/**
 * Pull the snapshot out of whatever shape the endpoint returned.
 *
 * Accepting three shapes rather than one is not laxity: `{ ok, data }` is the
 * house envelope of most APIs this will point at, and forcing every caller to
 * write an adapter for it would push them straight back to hand-rolling the
 * client this module exists to replace.
 */
function extractSnapshot(body: unknown): FlagSnapshot {
  if (!body || typeof body !== 'object') return {};
  const record = body as Record<string, unknown>;

  const nested =
    (record.flags as unknown) ??
    ((record.data as Record<string, unknown> | undefined)?.flags as unknown);
  const target = nested && typeof nested === 'object' ? nested : record;

  const out: FlagSnapshot = {};
  for (const [key, value] of Object.entries(target as Record<string, unknown>)) {
    if (typeof value === 'boolean') out[key] = value;
  }
  return out;
}

/**
 * A flag engine backed by a caller-supplied source.
 *
 * Implements the same contract the bindings expect of `FeatureFlags`, so it is a
 * drop-in for `setFlagEngine()` and for any `engine` prop.
 */
export class FlagSource {
  private snapshot: FlagSnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly options: FlagSourceOptions;

  private timer: ReturnType<typeof setInterval> | null = null;
  private inFlight: Promise<void> | null = null;

  private readyPromise: Promise<void> | null = null;
  private readyResolve: (() => void) | null = null;
  private readyState = false;

  constructor(options: FlagSourceOptions = {}) {
    this.options = options;
    this.snapshot = { ...(options.initial ?? {}) };

    // A seed IS an answer. Marking ready here is what lets `useFlagsReady()` and
    // `await ready()` return immediately on a server-rendered page instead of
    // waiting on a fetch whose result the page already has.
    if (options.initial) this.markReady();

    const canFetch = Boolean(options.fetcher || options.endpoint);
    if (!canFetch) {
      // Nothing to wait for. Settling now keeps the "ready always settles"
      // promise true for a purely seeded or purely static source.
      this.markReady();
    } else if (!options.lazy) {
      void this.refresh();
    }

    if (canFetch && options.refreshMs && options.refreshMs > 0) {
      this.timer = setInterval(() => void this.refresh(), options.refreshMs);
      // Never hold a Node process open for a poll — this is a side effect of the
      // app, not a reason for it to keep running.
      (this.timer as unknown as { unref?: () => void }).unref?.();
    }
  }

  // --------------------------------------------------------------------------
  // Reads — synchronous, total, never throwing
  // --------------------------------------------------------------------------

  /** Is this flag on? Returns `fallback` when this source has no answer. */
  on(key: string, fallback = false): boolean {
    if (!key) return fallback;
    const value = this.snapshot[key];
    return typeof value === 'boolean' ? value : fallback;
  }

  /** `!on(key)`, for guards that read better in the negative. */
  off(key: string, fallback = false): boolean {
    return !this.on(key, fallback);
  }

  /** Every answer this source currently holds. */
  all(): FlagSnapshot {
    return { ...this.snapshot };
  }

  /** True once there is an answer — a seed, a fetch, or nothing to fetch. */
  get isReady(): boolean {
    return this.readyState;
  }

  /** Resolves once `isReady`. Always settles, including on a failed fetch. */
  ready(): Promise<void> {
    if (this.readyState) return Promise.resolve();
    if (!this.readyPromise) {
      this.readyPromise = new Promise<void>((resolve) => {
        this.readyResolve = resolve;
      });
    }
    return this.readyPromise;
  }

  // --------------------------------------------------------------------------
  // Writes
  // --------------------------------------------------------------------------

  /**
   * Replace the answers and wake subscribers — but only if something moved.
   *
   * The equality check is load-bearing rather than an optimisation: a refresh
   * returns fresh JSON every time, so notifying on arrival would re-render every
   * subscribed component on a timer even when no flag has changed in weeks.
   */
  set(next: FlagSnapshot): void {
    const changed = this.differs(next);
    this.snapshot = { ...next };
    this.markReady();
    if (changed) this.emit();
  }

  /** Merge a partial snapshot over the current one. */
  patch(partial: FlagSnapshot): void {
    this.set({ ...this.snapshot, ...partial });
  }

  /**
   * Fetch once from the configured source.
   *
   * Concurrent calls share one request. A failure leaves the previous snapshot
   * in place and still marks ready — a source that is down must not leave the UI
   * waiting forever for an answer that is not coming.
   */
  refresh(): Promise<void> {
    if (this.inFlight) return this.inFlight;

    const run = async () => {
      try {
        const next = this.options.fetcher
          ? await this.options.fetcher()
          : await this.fetchEndpoint();
        this.set(next);
      } catch (error) {
        this.options.onError?.(error);
        this.markReady();
      } finally {
        this.inFlight = null;
      }
    };

    this.inFlight = run();
    return this.inFlight;
  }

  /**
   * Subscribe to changes. Returns an unsubscribe function.
   *
   * A listener that throws must not stop the others from being told, so each is
   * called in isolation.
   */
  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Stop polling. Safe to call twice; reads keep working afterwards. */
  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  // --------------------------------------------------------------------------
  // Internals
  // --------------------------------------------------------------------------

  private async fetchEndpoint(): Promise<FlagSnapshot> {
    const endpoint = this.options.endpoint;
    if (!endpoint) return this.snapshot;
    if (typeof fetch !== 'function') return this.snapshot;

    const response = await fetch(endpoint, {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Flag source responded ${response.status}`);
    return extractSnapshot(await response.json());
  }

  private differs(next: FlagSnapshot): boolean {
    const before = Object.keys(this.snapshot);
    const after = Object.keys(next);
    if (before.length !== after.length) return true;
    return after.some((key) => this.snapshot[key] !== next[key]);
  }

  private emit(): void {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch {
        /* a subscriber's failure is not this engine's to propagate */
      }
    });
  }

  private markReady(): void {
    if (this.readyState) return;
    this.readyState = true;
    this.readyResolve?.();
    this.readyResolve = null;
    if (!this.readyPromise) this.readyPromise = Promise.resolve();
  }
}

/**
 * A flag engine reading from your own source.
 *
 * Pair with {@link setFlagEngine} to make it the answer for every binding, with
 * no provider and no context:
 *
 * ```ts
 * setFlagEngine(createFlagSource({ endpoint: '/api/my-flags' }));
 * ```
 */
export function createFlagSource(options: FlagSourceOptions = {}): FlagSource {
  return new FlagSource(options);
}

/**
 * The seed a server rendered into the page, if any.
 *
 * The convention is one global — `window.__FLOWGRID_FLAGS__` — holding evaluated
 * booleans. It is deliberately a plain global rather than a framework mechanism
 * so that *any* server can write it: Next, Rails, Django, Laravel, or a hand-
 * written `<script>` in a template from 2011.
 *
 * ```html
 * <script>window.__FLOWGRID_FLAGS__ = { "new-checkout": true };</script>
 * ```
 */
export function readSeededFlags(): FlagSnapshot | undefined {
  if (typeof globalThis === 'undefined') return undefined;
  const seed = (globalThis as unknown as { __FLOWGRID_FLAGS__?: unknown }).__FLOWGRID_FLAGS__;
  if (!seed || typeof seed !== 'object') return undefined;
  return extractSnapshot(seed);
}
